import React, { useState } from "react";
import { endpoints } from "config";
import * as Yup from "yup";
import { accessRightsRenderer } from "components/AccessRights/AccessRights";
import { DocumentFields, NestedDocumentFields } from "./models";
import { FieldRenderers } from "@avamae/formbuilder";
import { Data } from "./Documents";
import Entity from "components/Entity";
import {
  singleMultiSelectFieldRenderer,
  singleMultiSelectReadOnlyFieldRenderer,
} from "components/helpers/CustomFieldRenderers/SingleMultiSelectFieldRenderer.tsx/SingleMultiSelectFieldRenderer";
import { searchListFieldRenderer } from "components/helpers/CustomFieldRenderers/SearchListFieldRenderer/SearchListRenderer";
import {
  actionLinkTextConfig,
  endpointConfig,
  linkConfig,
  nestedEndpointConfig,
  onActionLinkClick,
  onLinkClick,
} from "./documentConfig";
import { multiFileUploadFieldRenderer } from "components/helpers/FieldRenderers/FileUpload/multiFileUploadRenderer";
import { booleanV2Renderer } from "components/BooleanV2/BooleanV2";
import { dateTimeV2Renderer } from "components/DateTimeFieldV2/DateTimeFieldV2";
import {
  AxiosRequestConfigOrCallback,
  ConditionalFields,
  FormDatasourceData,
  FormFieldRendererOverride,
  parseFormDatasourceData,
} from "components/Entity/Entity";
import { CrudOptions } from "components/Category/CrudMenu";
import { stringReadOnlyEditRenderer } from "components/helpers/CustomFieldRenderers/CustomString/StringReadOnlyEditFieldRenderer";
import { CustomFileViewer as FileViewer } from "./helpers/FileViewer";
import {
  requiredBoolean,
  requiredId,
  optionalString,
  optionalArray,
  optionalId,
  requiredFileArray,
  optionalFileArray,
} from "helpers/validation";

const getCreateValidationSchema = (formDatasourceData: FormDatasourceData | null) => {
  const parsedFormDatasourceData = parseFormDatasourceData(formDatasourceData);

  const programmesIDsSchema =
    parsedFormDatasourceData &&
    parsedFormDatasourceData.hasOwnProperty("listDocumentLevels") &&
    parsedFormDatasourceData["listDocumentLevels"].hasOwnProperty("programme")
      ? Yup.array()
          .of(Yup.number())
          .nullable()
          .when("documentLevelsID", {
            is: parsedFormDatasourceData["listDocumentLevels"]["programme"],
            then: (schema) => schema.required("Please provide programmes"),
            otherwise: (schema) => schema,
          })
      : optionalArray(Yup.number());

  const sitesIDsSchema =
    parsedFormDatasourceData &&
    parsedFormDatasourceData.hasOwnProperty("listDocumentLevels") &&
    parsedFormDatasourceData["listDocumentLevels"].hasOwnProperty("site")
      ? Yup.array()
          .of(Yup.number())
          .nullable()
          .when("documentLevelsID", {
            is: parsedFormDatasourceData["listDocumentLevels"]["site"],
            then: (schema) => schema.required("Please provide sites"),
            otherwise: (schema) => schema,
          })
      : optionalArray(Yup.number());

  return Yup.object({
    bActive: requiredBoolean("Please select if the Document is active"),
    bFavourite: requiredBoolean("Please select if the Document is a favourite"),
    description: optionalString(),
    documentTypesID: requiredId("Please select a Document type"),
    documentLevelsID: requiredId("please select a Document Level"),
    files: requiredFileArray("Please upload file(s)"),
    name: optionalString(),
    programmesIDs: programmesIDsSchema,
    sitesIDs: sitesIDsSchema,
  });
};

const getEditValidationSchema = (formDatasourceData: FormDatasourceData | null) => {
  const parsedFormDatasourceData = parseFormDatasourceData(formDatasourceData);

  const programmesIDsSchema =
    parsedFormDatasourceData &&
    parsedFormDatasourceData.hasOwnProperty("listDocumentLevels") &&
    parsedFormDatasourceData["listDocumentLevels"].hasOwnProperty("programme")
      ? Yup.array()
          .of(Yup.number())
          .nullable()
          .when("documentLevelsID", {
            is: parsedFormDatasourceData["listDocumentLevels"]["programme"],
            then: (schema) => schema.required("Please provide programmes"),
            otherwise: (schema) => schema,
          })
      : optionalArray(Yup.number());

  const sitesIDsSchema =
    parsedFormDatasourceData &&
    parsedFormDatasourceData.hasOwnProperty("listDocumentLevels") &&
    parsedFormDatasourceData["listDocumentLevels"].hasOwnProperty("site")
      ? Yup.array()
          .of(Yup.number())
          .nullable()
          .when("documentLevelsID", {
            is: parsedFormDatasourceData["listDocumentLevels"]["site"],
            then: (schema) => schema.required("Please provide sites"),
            otherwise: (schema) => schema,
          })
      : optionalArray(Yup.number());

  return Yup.object({
    bActive: requiredBoolean("Please select if the Document is active"),
    bFavourite: requiredBoolean("Please select if the Document is a favourite"),
    description: optionalString(),
    files: optionalFileArray(),
    documentTypesID: requiredId("Please select a Document type"),
    documentLevelsID: requiredId("please select a Document Level"),
    name: optionalString(),
    programmesIDs: programmesIDsSchema,
    sitesIDs: sitesIDsSchema,
    projectsIDs: optionalArray(Yup.number()),
  });
};

const getNestedCreateValidationSchema = (formDatasourceData: FormDatasourceData | null) => {
  const parsedFormDatasourceData = parseFormDatasourceData(formDatasourceData);

  const programmesIDsSchema =
    parsedFormDatasourceData &&
    parsedFormDatasourceData.hasOwnProperty("listDocumentLevels") &&
    parsedFormDatasourceData["listDocumentLevels"].hasOwnProperty("programme")
      ? Yup.array()
          .of(Yup.number())
          .nullable()
          .when("documentLevelsID", {
            is: parsedFormDatasourceData["listDocumentLevels"]["programme"],
            then: (schema) => schema.required("Please provide programmes"),
            otherwise: (schema) => schema,
          })
      : optionalArray(Yup.number());

  return Yup.object({
    bActive: requiredBoolean("Please select if the Document is active"),
    bFavourite: requiredBoolean("Please select if the Document is a favourite"),
    description: optionalString(),
    documentTypesID: requiredId("Please select a Document type"),
    files: requiredFileArray("Please upload file(s)"),
    name: optionalString(),
    programmesIDs: programmesIDsSchema,
    projectsID: optionalId(),
  });
};

const getNestedEditValidationSchema = (formDatasourceData: FormDatasourceData | null) => {
  const parsedFormDatasourceData = parseFormDatasourceData(formDatasourceData);

  const programmesIDsSchema =
    parsedFormDatasourceData &&
    parsedFormDatasourceData.hasOwnProperty("listDocumentLevels") &&
    parsedFormDatasourceData["listDocumentLevels"].hasOwnProperty("programme")
      ? Yup.array()
          .of(Yup.number())
          .nullable()
          .when("documentLevelsID", {
            is: parsedFormDatasourceData["listDocumentLevels"]["programme"],
            then: (schema) => schema.required("Please provide programmes"),
            otherwise: (schema) => schema,
          })
      : optionalArray(Yup.number());

  return Yup.object({
    bActive: requiredBoolean("Please select if the Document is active"),
    bFavourite: Yup.boolean().nullable().required("Please select if the Document is a favourite"),

    description: Yup.string().nullable(),
    documentTypesID: Yup.number().nullable().required("Please select a Document type"),
    files: optionalFileArray(),
    name: Yup.string().nullable(),

    programmesIDs: Yup.array().of(Yup.number()).nullable(),

    projectsID: Yup.number().nullable(),
  });
};

type Props = {
  actionType: "create" | "edit" | "view";
  selectedData: Data[];
  onDone(): void;
  idFromRowOption: React.ReactText | null;
  additionalData?: any;
  documentVersionNumber?: string;
  nestedListPageUrl?: string;
  parentSelectedId?: number;
  autoMenuActionsCode?: CrudOptions;
  clickedIdIfMultiIds?: string | number;
  noSubmit?: boolean;
};

enum DocumentLevels {
  "SITE" = "SITE",
  "PROGRAMME" = "PROGRAMME",
  "PROJECT" = "PROJECT",
}

const fetchConfig = (
  selectedRows: Data[],
  idFromRowOption?: React.ReactText | null,
  additionalData?: any[],
  parentSelectedId?: number,
  clickedIdIfMultiIds?: number | string
) => {
  const documentsId = selectedRows.find(
    (r) =>
      String(r.documents__Id) === String(clickedIdIfMultiIds) ||
      Number(r.documents__Id) === Number(clickedIdIfMultiIds)
  )?.documents__Id;
  const Id =
    idFromRowOption ??
    (clickedIdIfMultiIds
      ? (documentsId ?? selectedRows[0].documents__Id)
      : selectedRows[0].documents__Id);

  return {
    params: {
      Id,

      VersionNumber: additionalData?.find((d: any) => String(d.id) === String(Id))
        ?.documents__DocumentVersion,

      ProjectsId: parentSelectedId ?? undefined,
    },
  };
};

export const documentsRightsRenderer = (): FieldRenderers<DocumentFields> => ({
  boolean: booleanV2Renderer(false),
  accessrights: accessRightsRenderer(false),
  singleselect: singleMultiSelectFieldRenderer("singleselect"),
  singleselectradio: singleMultiSelectFieldRenderer("singleselectradio"),
  multiselecteditdisabled: searchListFieldRenderer(
    "multiselectWithLinks",
    {
      searchEndpoint: endpointConfig,
      linkTextConfig: linkConfig,
      onLinkClick: onLinkClick,
      actionLinkConfig: actionLinkTextConfig,
      handleActionLinkClicked: onActionLinkClick,
    },
    null,
    true
  ),
  multiselectsearchlistwithlinks: searchListFieldRenderer("multiselectWithLinks", {
    searchEndpoint: endpointConfig,
    linkTextConfig: linkConfig,
    onLinkClick: onLinkClick,
    actionLinkConfig: actionLinkTextConfig,
    handleActionLinkClicked: onActionLinkClick,
  }),
  singleselectsearchlistwithlinks: searchListFieldRenderer("singleSelectWithLinks", {
    searchEndpoint: endpointConfig,
    linkTextConfig: linkConfig,
    onLinkClick: onLinkClick,
    actionLinkConfig: actionLinkTextConfig,
    handleActionLinkClicked: onActionLinkClick,
  }),
  file: multiFileUploadFieldRenderer({
    readOnly: false,
    fieldNames: {
      singleImageOrFile: {
        imageOrFile: "file",
        imageOrFileName: "fileName",
        imageOrFileDownloadUri: "fileDownloadUri",
        imageOrFileExtension: "fileExtension",
      },
      multipleImagesOrFiles: "files",
    },
    isMulti: true,
    acceptedFileTypes: {
      //files: ["image/jpg", "image/png", "application/pdf"],
    },
    resetToClearAll: true,
  }),
  datetime: dateTimeV2Renderer(false),
  stringreadonly: stringReadOnlyEditRenderer(null, null, false),
  integerreadonly: stringReadOnlyEditRenderer(null, null, true),
});

export const documentsReadOnlyRightsRenderer = (): FieldRenderers<DocumentFields> => ({
  accessrights: accessRightsRenderer(true),
  multiselect: singleMultiSelectReadOnlyFieldRenderer("multiselect"),
  datetime: dateTimeV2Renderer(true),
});

export const documentsOverrideFormReadOnlyFieldRenderer: FormFieldRendererOverride<any> = {
  filePath: (_key, field) => <FileViewer field={field} readOnly={true} />,
};

const nestedRightsRenderer = (): FieldRenderers<NestedDocumentFields> => ({
  boolean: booleanV2Renderer(false),
  accessrights: accessRightsRenderer(false),
  singleselect: singleMultiSelectFieldRenderer(
    "singleselect",
    undefined,
    undefined,
    undefined,
    true
  ),
  singleselectradio: singleMultiSelectFieldRenderer("singleselectradio"),
  multiselectsearchlistwithlinks: searchListFieldRenderer("multiselectWithLinks", {
    searchEndpoint: nestedEndpointConfig,
    linkTextConfig: linkConfig,
    onLinkClick: onLinkClick,
    actionLinkConfig: actionLinkTextConfig,
    handleActionLinkClicked: onActionLinkClick,
  }),
  singleselectsearchlistwithlinks: searchListFieldRenderer("singleSelectWithLinks", {
    searchEndpoint: nestedEndpointConfig,
    linkTextConfig: linkConfig,
    onLinkClick: onLinkClick,
    actionLinkConfig: actionLinkTextConfig,
    handleActionLinkClicked: onActionLinkClick,
  }),
  file: multiFileUploadFieldRenderer({
    readOnly: false,
    fieldNames: {
      singleImageOrFile: {
        imageOrFile: "file",
        imageOrFileName: "fileName",
        imageOrFileDownloadUri: "fileDownloadUri",
        imageOrFileExtension: "fileExtension",
      },
      multipleImagesOrFiles: "files",
    },
    isMulti: true,
    acceptedFileTypes: {},
    resetToClearAll: true,
  }),
  datetime: dateTimeV2Renderer(false),
  stringreadonly: stringReadOnlyEditRenderer(null, null, false),
  integerreadonly: stringReadOnlyEditRenderer(null, null, true),
});

const nestedReadOnlyRightsRenderer = (): FieldRenderers<NestedDocumentFields> => ({
  accessrights: accessRightsRenderer(true),
  multiselectsearchlistwithlinks: singleMultiSelectReadOnlyFieldRenderer("multiselect"),
  datetime: dateTimeV2Renderer(true),
});

function Document(props: Props): JSX.Element {
  const {
    actionType,
    selectedData,
    idFromRowOption,
    nestedListPageUrl,
    parentSelectedId,
    additionalData,
    autoMenuActionsCode,
    clickedIdIfMultiIds,
    noSubmit,
  } = props;

  const [formDatasourceData, setFormDatasourceData] = useState<FormDatasourceData | null>(null);

  let initialDataUrl =
    actionType === "edit" ? endpoints.documents.edit : endpoints.documents.create;
  /* REPLACE__ME */

  if (actionType === "view") initialDataUrl = endpoints.documents.view; // REPLACE__ME

  if (nestedListPageUrl) {
    initialDataUrl = nestedListPageUrl;
  }

  let finalFetchConfig: AxiosRequestConfigOrCallback<Data> | undefined = undefined;
  if (parentSelectedId) {
    if (actionType === "create") {
      finalFetchConfig = undefined;
    } else if (actionType === "view") {
      finalFetchConfig = fetchConfig(
        selectedData,
        idFromRowOption,
        additionalData,
        parentSelectedId,
        clickedIdIfMultiIds
      );
    } else {
      finalFetchConfig = fetchConfig(
        selectedData,
        idFromRowOption,
        additionalData,
        parentSelectedId,
        clickedIdIfMultiIds
      );
    }
  } else {
    if (actionType === "create") {
      finalFetchConfig = undefined;
    } else {
      finalFetchConfig = fetchConfig(
        selectedData,
        idFromRowOption,
        additionalData,
        undefined,
        clickedIdIfMultiIds
      );
    }
  }

  const createValidationSchema = getCreateValidationSchema(formDatasourceData);
  const editValidationSchema = getEditValidationSchema(formDatasourceData);
  const nestedCreateValidationSchema = getNestedCreateValidationSchema(formDatasourceData);
  const nestedEditValidationSchema = getNestedEditValidationSchema(formDatasourceData);

  let conditionalFields: ConditionalFields = {
    ProgrammesIDs: {
      dependentField: "DocumentLevelsID",

      dependentValues: [DocumentLevels.PROGRAMME], // must be lowercase
      useLabelInsteadOfId: true,
      datasourceField: "listDocumentLevels",
    },
    SitesIDs: {
      dependentField: "DocumentLevelsID",

      dependentValues: [DocumentLevels.SITE],
      useLabelInsteadOfId: true,
      datasourceField: "listDocumentLevels",
    },
    ProjectsIDs: {
      dependentField: "DocumentLevelsID",
      dependentValues: [DocumentLevels.PROJECT],
      datasourceField: "listDocumentLevels",
    },
  };

  if (actionType !== "view") {
    conditionalFields.Programmes = {
      dependentField: "DocumentLevelsName",
      dependentValues: ["Programme"],
    };
    conditionalFields.Sites = {
      dependentField: "DocumentLevelsName",
      dependentValues: ["Site"],
    };

    conditionalFields.Projects = {
      dependentField: "DocumentLevelsName",
      dependentValues: ["Project"],
    };
  }

  if (!parentSelectedId) {
    return (
      <Entity
        initialDataUrl={initialDataUrl}
        readOnly={actionType === "view"}
        submitUrl={initialDataUrl}
        onDone={props.onDone}
        selectedData={selectedData}
        idFromRowOption={idFromRowOption}
        noSubmit={noSubmit}
        hideActionButton={actionType === "view"}
        hiddenFields={actionType === "create" ? ["projectsIDs"] : []}
        readOnlyFields={
          actionType === "create" || actionType === "edit"
            ? [
                {
                  fieldName: "Id",
                  readOnlyRenderer: "integerreadonly",
                },
              ]
            : undefined
        }
        formType="multipart"
        fieldRenderers={documentsRightsRenderer}
        validationSchema={actionType === "edit" ? editValidationSchema : createValidationSchema}
        readOnlyFieldRenderers={documentsReadOnlyRightsRenderer}
        nullStringValues={[]}
        fetchConfig={finalFetchConfig}
        groupColumns={actionType === "create" ? { document: 2 } : undefined}
        conditionalFields={conditionalFields}
        hideGroupLabel
        autoMenuActionsCode={
          actionType.toLowerCase() === autoMenuActionsCode?.toLowerCase()
            ? autoMenuActionsCode
            : undefined
        }
        clickedIdIfMultiIds={clickedIdIfMultiIds}
        overrideFormReadOnlyFieldRenderer={documentsOverrideFormReadOnlyFieldRenderer}
        formDatasourceData={formDatasourceData}
        setFormDatasourceData={setFormDatasourceData}
        showUploadProgress={true}
        uploadProgressText="Uploading document(s):"
      />
    );
  } else {
    return (
      <Entity
        initialDataUrl={initialDataUrl}
        readOnly={actionType === "view"}
        submitUrl={initialDataUrl}
        onDone={props.onDone}
        selectedData={selectedData}
        idFromRowOption={idFromRowOption}
        noSubmit={noSubmit}
        hideActionButton={actionType === "view"}
        formType="multipart"
        fieldRenderers={nestedRightsRenderer}
        validationSchema={
          actionType === "edit" ? nestedEditValidationSchema : nestedCreateValidationSchema
        }
        readOnlyFieldRenderers={nestedReadOnlyRightsRenderer}
        nullStringValues={[]}
        readOnlyFields={
          actionType === "create" || actionType === "edit"
            ? [
                {
                  fieldName: "Id",
                  readOnlyRenderer: "integerreadonly",
                },
              ]
            : undefined
        }
        fetchConfig={finalFetchConfig}
        groupColumns={actionType === "create" ? { document: 2 } : undefined}
        conditionalFields={undefined}
        hideAllGroupLabels
        overrideParamIds={
          parentSelectedId && (actionType === "create" || actionType === "edit")
            ? [
                {
                  idParamOverrideName: "projectsID",
                  idParamOverrideValue: parentSelectedId,
                },
              ]
            : undefined
        }
        overrideFormFieldRenderer={
          parentSelectedId && actionType === "create"
            ? {
                projectsID: searchListFieldRenderer(
                  "singleSelectWithLinks",
                  undefined,
                  undefined,
                  true
                ),
                // projectsIDs:  singleMultiSelectFieldRenderer("singleselect"),
              }
            : parentSelectedId && actionType === "edit"
              ? {
                  projectsID: searchListFieldRenderer(
                    "singleSelectWithLinks",
                    undefined,
                    undefined,
                    true
                  ),
                  // projectsIDs:  singleMultiSelectFieldRenderer("singleselect"),
                }
              : undefined
        }
        autoMenuActionsCode={
          actionType.toLowerCase() === autoMenuActionsCode?.toLowerCase()
            ? autoMenuActionsCode
            : undefined
        }
        clickedIdIfMultiIds={clickedIdIfMultiIds}
        overrideFormReadOnlyFieldRenderer={documentsOverrideFormReadOnlyFieldRenderer}
        formDatasourceData={formDatasourceData}
        setFormDatasourceData={setFormDatasourceData}
        showUploadProgress={true}
        uploadProgressText="Uploading document(s):"
      />
    );
  }
}

export default Document;
